import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { DEFAULT_LANGUAGE, translate } from './translate'
import type { LangCode } from './translations'

export type { LangCode }

export const SUPPORTED_LANGUAGES: { code: LangCode; name: string; nativeName: string; flag: string; dir?: 'rtl' | 'ltr' }[] = [
  { code: 'en', name: 'English',    nativeName: 'English',    flag: '🇺🇸' },
  { code: 'hi', name: 'Hindi',      nativeName: 'हिन्दी',      flag: '🇮🇳' },
  { code: 'es', name: 'Spanish',    nativeName: 'Español',    flag: '🇪🇸' },
  { code: 'fr', name: 'French',     nativeName: 'Français',   flag: '🇫🇷' },
  { code: 'de', name: 'German',     nativeName: 'Deutsch',    flag: '🇩🇪' },
  { code: 'zh', name: 'Chinese',    nativeName: '中文',        flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese',   nativeName: '日本語',      flag: '🇯🇵' },
  { code: 'ko', name: 'Korean',     nativeName: '한국어',      flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic',     nativeName: 'العربية',    flag: '🇸🇦', dir: 'rtl' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português',  flag: '🇵🇹' },
  { code: 'ru', name: 'Russian',    nativeName: 'Русский',    flag: '🇷🇺' },
  { code: 'it', name: 'Italian',    nativeName: 'Italiano',   flag: '🇮🇹' },
]

/** The one place the chosen language is kept — pre-auth and signed in alike. */
const LANGUAGE_STORAGE_KEY = 'app-language'

const isSupported = (code: unknown): code is LangCode =>
  SUPPORTED_LANGUAGES.some(l => l.code === code)

// localStorage throws (not just returns null) when site data is blocked, so a
// bare call here took the whole app down before the sign-in page could render.
function readStoredLanguage(): LangCode {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return isSupported(stored) ? stored : DEFAULT_LANGUAGE
  } catch {
    return DEFAULT_LANGUAGE
  }
}

function writeStoredLanguage(code: LangCode) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code)
  } catch {
    /* storage blocked — the choice still holds for this tab */
  }
}

type LanguageContextType = {
  language: LangCode
  setLanguage: (code: LangCode) => void
  t: (key: string, vars?: Record<string, string | number>) => string
  isRTL: boolean
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLang] = useState<LangCode>(readStoredLanguage)

  // The layout stays left-to-right in every language, Arabic included: the
  // product keeps cards, sentences and controls where they are and translates
  // only the text. Within each line the browser still orders Arabic words
  // right-to-left on its own. `lang` is still set, for fonts and screen readers.
  useLayoutEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = 'ltr'
  }, [language])

  // Another tab changed the language (from Settings or the sign-in page):
  // follow it here too, rather than leaving this tab on the old one until a
  // reload — after which the two would disagree the other way round.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && isSupported(e.newValue)) setLang(e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setLanguage = useCallback((code: LangCode) => {
    if (!isSupported(code)) return
    writeStoredLanguage(code)
    setLang(code)
  }, [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => translate(language, key, vars),
    [language],
  )

  const value = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    t,
    isRTL: SUPPORTED_LANGUAGES.find(l => l.code === language)?.dir === 'rtl',
  }), [language, setLanguage, t])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}

export function useTranslation() {
  return useLanguage().t
}
